declare const bootstrap: any;
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { AdminKycApplicationDetailsDto, AdminKycApplicationDocumentDto } from '../../../_core/_model/compliance';
import { ComplianceApiService } from '../../../_core/_services/_compliance/compliance-api.service';
import { NotificationService } from '../../../_core/_services/notification.service';

@Component({
  selector: 'app-admin-kyc-application-details',
  standalone: false,
  templateUrl: './admin-kyc-application-details.html',
  styleUrl: './admin-kyc-application-details.css',
})

export class AdminKycApplicationDetails implements OnInit {
  applicationId = '';
  loading = false;
  actionLoading = false;
  error: string | null = null;
  application: AdminKycApplicationDetailsDto | null = null;

  reviewNotes = '';
  rejectionReason = '';
  documentReviewNotes: Record<string, string> = {};
  documentRejectReasons: Record<string, string> = {};

  reopenReason = '';
  reopening = false;
  reopenConfirmed = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: ComplianceApiService,
    private notifyService: NotificationService,
  ) { }

  ngOnInit(): void {
    this.applicationId = this.route.snapshot.paramMap.get('applicationId') || '';

    if (!this.applicationId) {
      this.error = 'Application not found.';
      this.notifyService.showError(this.error!, 'Error');
      return;
    }

    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;

    this.api.getAdminReviewById(this.applicationId)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res) => {
          this.application = res.data || null;
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to load admin application details.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  startReview(): void {
    if (!this.application) return;

    this.actionLoading = true;
    this.api.startReview(this.application.applicationId, this.reviewNotes)
      .pipe(finalize(() => (this.actionLoading = false)))
      .subscribe({
        next: () => this.load(),
        error: (err) => {
          this.error = err?.error?.message || 'Failed to start review.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  approveApplication(): void {
    if (!this.application) return;

    this.actionLoading = true;
    this.api.approveApplication(this.application.applicationId, this.reviewNotes)
      .pipe(finalize(() => (this.actionLoading = false)))
      .subscribe({
        next: () => this.load(),
        error: (err) => {
          this.error = err?.error?.message || 'Failed to approve application.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  rejectApplication(): void {
    if (!this.application) return;

    if (!this.rejectionReason.trim()) {
      this.error = 'Rejection reason is required.';
      this.notifyService.showError(this.error!, 'Error');
      return;
    }

    this.actionLoading = true;
    this.api.rejectApplication(this.application.applicationId, this.rejectionReason)
      .pipe(finalize(() => (this.actionLoading = false)))
      .subscribe({
        next: () => this.load(),
        error: (err) => {
          this.error = err?.error?.message || 'Failed to reject application.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  approveDocument(doc: AdminKycApplicationDocumentDto): void {
    this.actionLoading = true;
    this.api.approveDocument(doc.id, this.documentReviewNotes[doc.id] || '')
      .pipe(finalize(() => (this.actionLoading = false)))
      .subscribe({
        next: () => this.load(),
        error: (err) => {
          this.error = err?.error?.message || 'Failed to approve document.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  rejectDocument(doc: AdminKycApplicationDocumentDto): void {
    const reason = (this.documentRejectReasons[doc.id] || '').trim();

    if (!reason) {
      this.error = 'Document rejection reason is required.';
      this.notifyService.showError(this.error!, 'Error');
      return;
    }

    this.actionLoading = true;
    this.api.rejectDocument(doc.id, reason)
      .pipe(finalize(() => (this.actionLoading = false)))
      .subscribe({
        next: () => this.load(),
        error: (err) => {
          this.error = err?.error?.message || 'Failed to reject document.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  getStatusClass(status?: string): string {
    switch (status) {
      case 'Approved':
      case 'ThirdPartyVerified':
      case 'VERIFIED':
        return 'text-bg-success';

      case 'Rejected':
      case 'ThirdPartyRejected':
      case 'REJECTED':
      case 'FAILED':
        return 'text-bg-danger';

      case 'Submitted':
      case 'UnderReview':
      case 'PendingThirdPartyVerification':
      case 'PENDING':
        return 'text-bg-warning';

      default:
        return 'text-bg-secondary';
    }
  }

  syncThirdPartyVerification(): void {
    if (!this.application) return;

    this.actionLoading = true;
    this.api.syncThirdPartyVerification(this.application.applicationId)
      .pipe(finalize(() => (this.actionLoading = false)))
      .subscribe({
        next: () => {
          this.notifyService.showSuccess('Blaaiz verification status synced.', 'Success');
          this.load();
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to sync Blaaiz verification status.';
          this.notifyService.showError(this.error!, 'Error');
        }
      });
  }

  back(): void {
    this.router.navigate(['/admin/compliance']);
  }

  openReopenModal(): void {
    this.reopenReason = '';
    this.reopenConfirmed = false;

    const element = document.getElementById('reopenModal');
    if (!element) return;

    const modal = bootstrap.Modal.getOrCreateInstance(element);
    modal.show();
  }

  reopenApplication(): void {
    if (!this.application || this.reopening) {
      return;
    }

    const reason = this.reopenReason.trim();

    if (!reason) {
      this.notifyService.showError(
        'A reason is required to reopen this application.',
        'Compliance'
      );
      return;
    }

    if (!this.reopenConfirmed) {
      this.notifyService.showError(
        'Please confirm that you understand the impact of reopening this application.',
        'Compliance'
      );
      return;
    }

    this.reopening = true;

    this.api.reopenApplication(
      this.application.applicationId,
      reason
    )
      .pipe(
        finalize(() => {
          this.reopening = false;
        })
      )
      .subscribe({
        next: (res) => {
          const element = document.getElementById('reopenModal');

          if (element) {
            bootstrap.Modal.getInstance(element)?.hide();
          }

          this.reopenReason = '';
          this.reopenConfirmed = false;

          this.notifyService.showSuccess(
            res?.message || 'Application reopened successfully.',
            'Compliance'
          );

          this.load();
        },
        error: (err) => {
          const message =
            err?.error?.error?.details ||
            err?.error?.Error?.Details ||
            err?.error?.message ||
            err?.error?.Message ||
            'Failed to reopen application.';

          this.notifyService.showError(
            message,
            'Compliance'
          );
        }
      });
  }

  trackByField = (_: number, item: { fieldKey: string }) => item.fieldKey;
  trackByDoc = (_: number, item: { id: string }) => item.id;
}